import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, AlertCircle, ShieldAlert } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VariableReferencePanel } from '@/components/VariableReferencePanel';
import { SectionEditor } from '@/components/SectionEditor';
import { FullTemplateEditor } from '@/components/FullTemplateEditor';
import { PreviewPanel } from '@/components/PreviewPanel';
import { TestEmailForm } from '@/components/TestEmailForm';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface EmailTemplate {
  type: string;
  name: string;
  description: string;
  variableCount: number;
}

interface EmailTemplateSchema {
  type: string;
  name: string;
  description: string;
  variables: Array<{
    name: string;
    key: string;
    description: string;
    example: string;
    required: boolean;
    dataType: string;
    category?: string;
  }>;
  defaultSubject: string;
  defaultHtml: string;
  defaultText: string;
  sampleData: Record<string, any>;
}

interface CustomTemplate {
  subject?: string;
  htmlBody?: string;
  textBody?: string;
  sections?: {
    greeting?: string;
    intro?: string;
    callToAction?: string;
    footer?: string;
  };
  lastModified?: string;
  modifiedBy?: string;
}

export default function EmailTemplatesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string>('taskReminder');
  const [editMode, setEditMode] = useState<'sections' | 'full'>('sections');
  const [previewData, setPreviewData] = useState<any>(null);

  // Only owner users can access this page
  if (user?.role !== 'owner') {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] space-y-4">
        <ShieldAlert className="h-16 w-16 text-muted-foreground" />
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold">Access Denied</h2>
          <p className="text-muted-foreground">
            Only owner users can access email templates.
          </p>
        </div>
      </div>
    );
  }

  // Fetch template list
  const { data: templatesData, isLoading: templatesLoading, error: templatesError } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => apiGet<{ templates: EmailTemplate[] }>('/api/v1/email-templates'),
  });

  // Fetch template schema
  const { data: schemaData, isLoading: schemaLoading } = useQuery({
    queryKey: ['email-template-schema', selectedType],
    queryFn: () => apiGet<{ schema: EmailTemplateSchema }>(`/api/v1/email-templates/${selectedType}`),
    enabled: !!selectedType,
  });

  // Fetch custom template
  const { data: customData, refetch: refetchCustom } = useQuery({
    queryKey: ['email-template-custom', selectedType],
    queryFn: () => apiGet<{ customTemplate: CustomTemplate | null; hasCustomization: boolean }>(`/api/v1/email-templates/${selectedType}/custom`),
    enabled: !!selectedType,
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: (data: { customTemplate?: CustomTemplate; customData?: any }) =>
      apiPost(`/api/v1/email-templates/${selectedType}/preview`, data),
    onSuccess: (data) => {
      setPreviewData(data.rendered);
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (customTemplate: CustomTemplate | null) =>
      apiPut(`/api/v1/email-templates/${selectedType}/custom`, { customTemplate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-template-custom', selectedType] });
      refetchCustom();
    },
  });

  // Send test mutation
  const sendTestMutation = useMutation({
    mutationFn: (data: { toEmail: string; customTemplate?: CustomTemplate }) =>
      apiPost(`/api/v1/email-templates/${selectedType}/send-test`, data),
  });

  const handlePreview = (customTemplate?: CustomTemplate) => {
    previewMutation.mutate({ customTemplate: customTemplate || customData?.customTemplate });
  };

  const handleSave = (customTemplate: CustomTemplate | null) => {
    saveMutation.mutate(customTemplate);
  };

  const handleSendTest = (toEmail: string) => {
    sendTestMutation.mutate({
      toEmail,
      customTemplate: customData?.customTemplate || undefined,
    });
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset this template to default? This cannot be undone.')) {
      saveMutation.mutate(null);
      setPreviewData(null);
    }
  };

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading templates...</div>
      </div>
    );
  }

  if (templatesError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold">Failed to load email templates</h2>
          <p className="text-muted-foreground max-w-md">
            {templatesError instanceof Error ? templatesError.message : 'An error occurred while fetching templates'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Email Templates</h1>
        <p className="text-muted-foreground mt-1">
          Customize email templates sent by your organization
        </p>
      </div>

      {/* Template selector */}
      {(!templatesData?.templates || templatesData.templates.length === 0) && (
        <div className="text-center py-8 text-muted-foreground">
          No email templates found. This may indicate an API configuration issue.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {templatesData?.templates?.map((template) => (
          <button
            key={template.type}
            onClick={() => {
              setSelectedType(template.type);
              setPreviewData(null);
            }}
            className={`p-4 border rounded-lg transition-all hover:shadow-md ${
              selectedType === template.type
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card hover:bg-accent'
            }`}
          >
            <Mail className="h-5 w-5 mb-2" />
            <div className="text-sm font-medium">{template.name}</div>
            <div className="text-xs mt-1 opacity-80">{template.variableCount} variables</div>
          </button>
        ))}
      </div>

      {schemaLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading template details...</div>
        </div>
      ) : (
        <>
          {/* Description */}
          <div className="bg-muted/50 border rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">{schemaData?.schema?.name}</p>
                <p className="text-sm text-muted-foreground">{schemaData?.schema?.description}</p>
                {customData?.hasCustomization && (
                  <p className="text-xs text-primary mt-1">
                    ✓ Custom template active
                    {customData.customTemplate?.lastModified && (
                      <span className="text-muted-foreground">
                        {' '}
                        • Last modified {new Date(customData.customTemplate.lastModified).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Editor tabs */}
          <Tabs value={editMode} onValueChange={(v) => setEditMode(v as 'sections' | 'full')}>
            <TabsList>
              <TabsTrigger value="sections">Edit Sections</TabsTrigger>
              <TabsTrigger value="full">Full Template Override</TabsTrigger>
            </TabsList>

            <TabsContent value="sections" className="space-y-6">
              <SectionEditor
                schema={schemaData?.schema}
                customTemplate={customData?.customTemplate || null}
                onSave={handleSave}
                onPreview={handlePreview}
                onReset={handleReset}
                isSaving={saveMutation.isPending}
                isPreviewing={previewMutation.isPending}
              />
            </TabsContent>

            <TabsContent value="full" className="space-y-6">
              <FullTemplateEditor
                schema={schemaData?.schema}
                customTemplate={customData?.customTemplate || null}
                onSave={handleSave}
                onPreview={handlePreview}
                onReset={handleReset}
                isSaving={saveMutation.isPending}
                isPreviewing={previewMutation.isPending}
              />
            </TabsContent>
          </Tabs>

          {/* Variable reference panel */}
          {schemaData?.schema && <VariableReferencePanel variables={schemaData.schema.variables} />}

          {/* Preview panel */}
          {previewData && <PreviewPanel rendered={previewData} />}

          {/* Test email form */}
          <TestEmailForm
            onSend={handleSendTest}
            isSending={sendTestMutation.isPending}
            success={sendTestMutation.isSuccess}
            error={sendTestMutation.error?.message}
          />

          {/* Save status messages */}
          {saveMutation.isSuccess && (
            <div className="bg-success-muted border border-success-border text-success-muted-foreground rounded-lg p-4">
              ✓ Template saved successfully
            </div>
          )}
          {saveMutation.isError && (
            <div className="bg-error-muted border border-error-border text-error-foreground rounded-lg p-4">
              Error saving template: {saveMutation.error?.message}
            </div>
          )}
        </>
      )}
    </div>
  );
}
